import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { useAuth } from '../../../context/AuthContext';
import { useSession } from '../../../context/SessionContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';
import {
  PracticeHeader,
  PracticeAnswerGrid,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  PRACTICE_CRITERIA,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
} from '../shared';

interface AttemptRecord {
  factId: string;
  correct: boolean;
  timestamp: number;
}

interface AccuracyScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

// Component to show dots for recent question presentations (within 2-minute engagement window)
const AttemptDotsIndicator: React.FC<{ attempts: AttemptRecord[] }> = ({ attempts }) => {
  // Filter to attempts within the last 2 minutes, then take last 10
  const now = Date.now();
  const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;
  const recentAttempts = attempts.filter(a => a.timestamp >= windowStart);
  const last10 = recentAttempts.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

  const dots = [];
  for (let i = 0; i < PRACTICE_CRITERIA.ATTEMPT_WINDOW; i++) {
    const attempt = last10[i];
    let bgColor = 'bg-gray-300'; // Not yet attempted
    if (attempt) {
      bgColor = attempt.correct ? 'bg-green-500' : 'bg-red-500';
    }
    dots.push(
      <div
        key={i}
        className={`w-4 h-4 rounded-full ${bgColor} transition-colors duration-300`}
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {dots}
    </div>
  );
};

// Component to show transition message (inline, not overlay)
const TransitionMessage: React.FC<{ type: 'pass' | 'fail'; message: string }> = ({ type, message }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center"
    >
      <div className="text-8xl mb-8">
        {type === 'pass' ? '🎉' : '💪'}
      </div>
      <h1 className="text-4xl lg:text-5xl font-bold">
        <span className={`bg-gradient-to-r ${
          type === 'pass'
            ? 'from-green-500 to-emerald-600'
            : 'from-blue-600 to-purple-600'
        } text-transparent bg-clip-text`}>
          {message}
        </span>
      </h1>
    </motion.div>
  );
};

const AccuracyScreen: React.FC<AccuracyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();
  const { recordPageTransition } = useSession();
  const [currentFact, setCurrentFact] = useState<{ factId: string; digit: number } | null>(null);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);

  // Record page transition for Caliper tracking
  useEffect(() => {
    recordPageTransition('accuracy-practice');
  }, [recordPageTransition]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  // Attempt history tracking for pass/fail criteria
  const [attemptHistory, setAttemptHistory] = useState<AttemptRecord[]>([]);
  const attemptHistoryRef = useRef<AttemptRecord[]>([]);

  // Track if current question presentation has been answered (to ignore retries)
  const [currentQuestionAnswered, setCurrentQuestionAnswered] = useState(false);

  // Transition message state
  const [transitionMessage, setTransitionMessage] = useState<{ type: 'pass' | 'fail'; message: string } | null>(null);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Session start time for engagement tracking
  const [sessionStartTime] = useState<number>(Date.now());

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, playChime, isTTSPlaying, playAudio } = usePreKTTS();

  const currentDigit = currentFact?.digit;

  // Select next fact, avoiding the same digit as current
  const selectNextFact = useCallback(() => {
    if (factsToPractice.length <= 1) {
      return factsToPractice[0];
    }
    let nextFact;
    do {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      nextFact = factsToPractice[randomIndex];
    } while (nextFact.digit === currentDigit);
    return nextFact;
  }, [factsToPractice, currentDigit]);

  // Keep attemptHistoryRef in sync
  useEffect(() => {
    attemptHistoryRef.current = attemptHistory;
  }, [attemptHistory]);

  // Check pass/fail criteria (each question presentation counts, retries don't)
  const checkCriteria = useCallback(() => {
    const history = attemptHistoryRef.current;
    const now = Date.now();
    const windowStart = now - PRACTICE_CRITERIA.ENGAGEMENT_WINDOW_MS;

    // Get last 10 question presentations
    const last10 = history.slice(-PRACTICE_CRITERIA.ATTEMPT_WINDOW);

    // Get attempts in last 2 minutes
    const recentAttempts = history.filter(a => a.timestamp >= windowStart);

    // Need at least 10 question presentations to evaluate pass/fail
    if (last10.length < PRACTICE_CRITERIA.ATTEMPT_WINDOW) {
      return { shouldPass: false, shouldFail: false };
    }

    // Calculate accuracy over last 10 questions
    const correctCount = last10.filter(a => a.correct).length;
    const accuracy = correctCount / last10.length;

    // Check engagement (8+ questions answered in last 2 min)
    const hasEngagement = recentAttempts.length >= PRACTICE_CRITERIA.MIN_ENGAGEMENT_ATTEMPTS;

    // Pass criteria: 90%+ accuracy AND 8+ answers in last 2 min
    const shouldPass = accuracy >= PRACTICE_CRITERIA.PASS_ACCURACY_THRESHOLD && hasEngagement;

    // Fail criteria: Below 70% over last 10 attempts
    const shouldFailLowAccuracy = accuracy < PRACTICE_CRITERIA.FAIL_ACCURACY_THRESHOLD;

    // Soft fail after 25 attempts without passing (PreK attention span safeguard)
    const hasIncorrectInLast10 = correctCount < PRACTICE_CRITERIA.ATTEMPT_WINDOW;
    const shouldFailTooManyAttempts =
      history.length >= PRACTICE_CRITERIA.MAX_ATTEMPTS_BEFORE_SOFT_FAIL && hasIncorrectInLast10;

    const shouldFail = shouldFailLowAccuracy || shouldFailTooManyAttempts;

    return { shouldPass, shouldFail, last10 };
  }, []);

  // Handle pass - all facts to fluency15Practice
  const handlePass = useCallback(async () => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio
    setTransitionMessage({ type: 'pass', message: "Great job! Let's take the test!" });
    if (settings.speechEnabled) {
      playAudio(QUESTION_SCREEN_AUDIO.transitions.accuracyPass).catch(() => {});
    }

    try {
      const progressUpdate: SetUserProgressRequest = {
        facts: factsToPractice.reduce((acc, fact) => {
          acc[fact.factId] = { status: 'fluency15Practice' };
          return acc;
        }, {} as Record<string, { status: 'fluency15Practice' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update facts to fluency15Practice:', error);
      onComplete();
    }
  }, [user?.userId, factsToPractice, onComplete, settings.speechEnabled, playAudio]);

  // Handle fail - struggling facts to learning
  const handleFail = useCallback(async (last10: AttemptRecord[]) => {
    if (!user?.userId) {
      onComplete();
      return;
    }

    // Show transition message and play audio (use assessment failure audio)
    setTransitionMessage({ type: 'fail', message: "Nice try! Let's practice it first!" });
    if (settings.speechEnabled) {
      playAudio({ filename: 'results/result_fail_number_naming.mp3' }).catch(() => {});
    }

    try {
      // Get facts that were wrong in last 10
      const strugglingFactIds = new Set<string>();
      last10.forEach(attempt => {
        if (!attempt.correct) {
          strugglingFactIds.add(attempt.factId);
        }
      });

      if (strugglingFactIds.size === 0) {
        // No struggling facts, just reload after delay (3s to allow audio to finish)
        setTimeout(() => {
          window.location.reload();
        }, 3000);
        return;
      }

      const progressUpdate: SetUserProgressRequest = {
        facts: Array.from(strugglingFactIds).reduce((acc, factId) => {
          acc[factId] = { status: 'learning' };
          return acc;
        }, {} as Record<string, { status: 'learning' }>),
      };

      await setUserProgress(user.userId, progressUpdate);

      // Delay before redirect to show message (3s to allow audio to finish)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Failed to update struggling facts:', error);
      onComplete();
    }
  }, [user?.userId, onComplete, settings.speechEnabled, playAudio]);

  // Initialize first fact when component loads
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      const randomIndex = Math.floor(Math.random() * factsToPractice.length);
      setCurrentFact(factsToPractice[randomIndex]);
    }
  }, [currentFact, factsToPractice]);

  // Initialize answer choices when current fact changes (new question presentation)
  useEffect(() => {
    if (currentDigit !== undefined) {
      setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.NAMING, currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setCurrentQuestionAnswered(false); // Reset for new question presentation
      setQuestionStartTime(Date.now());
    }
  }, [currentFact]); // Depend on currentFact, not currentDigit, to detect new presentations

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && currentDigit !== undefined) {
      const audioFile =
        QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber];

      playTTSWithCallback(audioFile, () => {
        setTtsHasPlayed(true);
      }).catch(() => {
        setTtsHasPlayed(true);
      });
    } else if (!settings.speechEnabled) {
      setTtsHasPlayed(true);
    }
  }, [currentDigit, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const handleAnswerClick = useCallback(
    (selectedDigit: number) => {
      // Don't allow clicks while TTS is playing or already answered
      if (isTTSPlaying || hasAnswered || !currentFact) return;

      setHasAnswered(true);
      const isCorrect = selectedDigit === currentDigit;

      // Calculate response time
      const responseTime = Date.now() - questionStartTime;

      // Report attempt data to progress queue (all attempts for analytics)
      if (user?.userId && currentFact) {
        try {
          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting Pre-K number naming accuracy attempt:', error);
        }
      }

      // Only record FIRST attempt for this question presentation (retries don't count)
      if (!currentQuestionAnswered) {
        setCurrentQuestionAnswered(true);

        const newAttempt: AttemptRecord = {
          factId: currentFact.factId,
          correct: isCorrect,
          timestamp: Date.now(),
        };

        setAttemptHistory(prev => {
          const updated = [...prev, newAttempt];
          attemptHistoryRef.current = updated;
          return updated;
        });
      }

      if (isCorrect) {
        setCorrectAnswerGiven(true);

        // Play chime and check criteria
        playChime().then(() => {
          const timeoutId = setTimeout(() => {
            const { shouldPass, shouldFail, last10 } = checkCriteria();

            if (shouldPass) {
              handlePass();
            } else if (shouldFail && last10) {
              handleFail(last10);
            } else {
              // Continue practice - select new fact (avoiding same digit)
              setCurrentFact(selectNextFact());
              setHasAnswered(false);
              setCorrectAnswerGiven(false);
              setTtsHasPlayed(false);
            }
          }, 500);
          timeoutsRef.current.push(timeoutId);
        });
      } else {
        // Incorrect answer

        // Check criteria after recording attempt
        const timeoutId = setTimeout(() => {
          const { shouldPass, shouldFail, last10 } = checkCriteria();

          if (shouldPass) {
            handlePass();
          } else if (shouldFail && last10) {
            handleFail(last10);
          } else {
            // Continue practice - allow retry
            setHasAnswered(false);
          }
        }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
        timeoutsRef.current.push(timeoutId);
      }
    },
    [
      currentDigit,
      hasAnswered,
      isTTSPlaying,
      playChime,
      currentFact,
      user,
      questionStartTime,
      checkCriteria,
      handlePass,
      handleFail,
      factsToPractice,
      currentQuestionAnswered,
    ]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.ACCURACY}
        onBack={onBack}
        customProgress={<AttemptDotsIndicator attempts={attemptHistory} />}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6">
        {transitionMessage ? (
          <TransitionMessage type={transitionMessage.type} message={transitionMessage.message} />
        ) : (
          <PracticeAnswerGrid
            choices={answerChoices}
            correctAnswer={currentDigit ?? 0}
            hasAnswered={hasAnswered}
            isTTSPlaying={isTTSPlaying}
            onChoiceClick={handleAnswerClick}
          />
        )}
      </div>
    </div>
  );
};

export default AccuracyScreen;
